import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Calendar, TrendingUp, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RecurringInvoices() {
  const [recurring, setRecurring] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [minConfidence, setMinConfidence] = useState(50);

  useEffect(() => {
    fetchRecurringAnalysis();
  }, [minConfidence]);

  const fetchRecurringAnalysis = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/recurring?min_confidence=${minConfidence}`, { 
        withCredentials: true 
      });
      setRecurring(response.data.recurring_invoices || []);
      setSummary(response.data.summary || null);
    } catch (error) {
      console.error('Failed to fetch recurring analysis:', error);
      toast.error('Failed to load recurring invoices');
    } finally {
      setLoading(false);
    }
  };

  const getFrequencyBadge = (frequency) => {
    const colors = {
      'weekly': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      'bi-weekly': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      'monthly': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      'quarterly': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      'annual': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
      'irregular': 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
    };
    return colors[frequency] || colors['irregular'];
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-green-600 dark:text-green-400';
    if (confidence >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-orange-600 dark:text-orange-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
        <span className="ml-2 text-slate-600 dark:text-slate-400">Analyzing recurring patterns...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Confidence Filter */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-indigo-600" />
            Recurring Invoices
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Auto-detected subscription and recurring payments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600 dark:text-slate-400">Min Confidence:</label>
          <select
            value={minConfidence}
            onChange={(e) => setMinConfidence(Number(e.target.value))}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            data-testid="confidence-filter"
          >
            <option value={30}>30%+</option>
            <option value={50}>50%+</option>
            <option value={70}>70%+</option>
            <option value={90}>90%+</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
            <p className="text-sm opacity-80">Total Recurring</p>
            <p className="text-2xl font-bold">{summary.total_recurring_vendors}</p>
            <p className="text-xs opacity-70">vendors detected</p>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <p className="text-sm opacity-80">Monthly Subscriptions</p>
            <p className="text-2xl font-bold">{summary.monthly_recurring_count}</p>
            <p className="text-xs opacity-70">${summary.monthly_recurring_total?.toLocaleString()}/mo</p>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 text-white">
            <p className="text-sm opacity-80">Est. Annual Cost</p>
            <p className="text-2xl font-bold">${(summary.monthly_recurring_total * 12)?.toLocaleString()}</p>
            <p className="text-xs opacity-70">from monthly only</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white">
            <p className="text-sm text-slate-500 dark:text-slate-400">By Frequency</p>
            <div className="mt-1 space-y-1 text-xs">
              {summary.by_frequency?.monthly > 0 && <p>Monthly: {summary.by_frequency.monthly}</p>}
              {summary.by_frequency?.quarterly > 0 && <p>Quarterly: {summary.by_frequency.quarterly}</p>}
              {summary.by_frequency?.annual > 0 && <p>Annual: {summary.by_frequency.annual}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Recurring Invoices List */}
      {recurring.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 dark:bg-slate-800 rounded-xl">
          <AlertCircle className="w-12 h-12 mx-auto text-slate-400 mb-3" />
          <p className="text-slate-600 dark:text-slate-400">No recurring invoices detected yet</p>
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
            Upload more invoices to detect patterns
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {recurring.map((item, index) => (
            <div
              key={index}
              className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-slate-800"
            >
              <button
                onClick={() => setExpandedVendor(expandedVendor === index ? null : index)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                data-testid={`recurring-item-${index}`}
              >
                <div className="flex items-center gap-4">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white text-left">
                      {item.vendor_name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getFrequencyBadge(item.frequency)}`}>
                        {item.frequency || 'Unknown'}
                      </span>
                      <span className={`text-xs font-medium ${getConfidenceColor(item.confidence)}`}>
                        {item.confidence}% confidence
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-semibold text-slate-900 dark:text-white">
                      ${item.avg_amount?.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">avg amount</p>
                  </div>
                  {expandedVendor === index ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </button>
              
              {expandedVendor === index && (
                <div className="px-4 pb-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Invoice Count</p>
                      <p className="font-medium text-slate-900 dark:text-white">
                        {item.pattern_details?.invoice_count || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Avg Interval</p>
                      <p className="font-medium text-slate-900 dark:text-white">
                        {item.pattern_details?.avg_interval_days || 0} days
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Amount Variance</p>
                      <p className="font-medium text-slate-900 dark:text-white">
                        {item.amount_variance || 0}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Next Expected</p>
                      <p className="font-medium text-slate-900 dark:text-white flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {item.next_expected || 'Unknown'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Invoice History</p>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-slate-600 dark:text-slate-300">
                        First: {item.pattern_details?.first_invoice}
                      </span>
                      <span className="text-slate-400">→</span>
                      <span className="text-slate-600 dark:text-slate-300">
                        Last: {item.pattern_details?.last_invoice}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RecurringInvoices;
